import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import App from './App';
import './index.css';
import { initPwa } from './lib/pwa';
import { resetServiceWorkerAndCaches } from './lib/swReset';
import { ServiceGate } from './components/ServiceGate';

/**
 * Manual escape hatch: loading the SPA with `?clear-sw=1` (or
 * `?reset-sw=1`) wipes every CacheStorage bucket and unregisters every
 * service worker that's bound to this origin, then reloads the page
 * WITHOUT the query string. After this one-time round-trip the next
 * `GET /api/devices` lands on the network for real and the bundle
 * precache is rebuilt from the latest dist/.
 *
 * In practice users should NEVER need this knob anymore — the
 * `loadedAt`-based auto-reset wired into `useRuntimeConfig.fetchOnce`
 * triggers the same cleanup the instant it sees the helper restart.
 * We keep `?clear-sw=1` as a documented bottom-of-the-barrel fallback
 * for corrupted SW state where even the auto-detector won't fire (e.g.
 * the SW is wedged before any /api/config response makes it through).
 *
 * Runs BEFORE React mounts so the page never even tries to use the
 * stale SW for its initial navigation.
 */
async function maybeResetServiceWorker(): Promise<boolean> {
  if (typeof window === 'undefined') return false;
  const params = new URLSearchParams(window.location.search);
  if (!params.has('clear-sw') && !params.has('reset-sw')) return false;
  await resetServiceWorkerAndCaches();
  params.delete('clear-sw');
  params.delete('reset-sw');
  const qs = params.toString();
  const clean = window.location.pathname + (qs ? `?${qs}` : '') + window.location.hash;
  window.location.replace(clean);
  return true;
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      staleTime: 5_000,
      refetchOnWindowFocus: false,
    },
  },
});

// (Previously we prefetched the adb command catalog at boot to warm the
// "命令中心" page. With the SPA decoupled from the adh agent, the
// landing page is `/` not `/devices`, so first-paint never needs the
// catalog — and prefetching during adh-offline boots would just spend
// a failed cross-origin request. The per-page useQuery refetches on
// mount when the user actually opens 命令中心, which is fast enough.)

function bootstrap(): void {
  ReactDOM.createRoot(document.getElementById('root')!).render(
    <React.StrictMode>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          {/*
           * Kill-switch boundary. Mounts above <App> so the maintenance page
           * replaces the entire UI when ops flip service.enabled=false —
           * including the AuthGate inside App. ServiceGate also owns the
           * /api/config polling lifecycle.
           */}
          <ServiceGate>
            <App />
          </ServiceGate>
        </BrowserRouter>
      </QueryClientProvider>
    </React.StrictMode>
  );

  // Register the service worker AFTER first paint so it never competes with
  // the critical render path. UpdateToast subscribes synchronously inside
  // initPwa(), so any onNeedRefresh / onOfflineReady events that fire during
  // registration are buffered by virtual:pwa-register and replayed on the
  // next listener tick.
  if (typeof window !== 'undefined') {
    window.addEventListener('load', () => initPwa(), { once: true });
  }
}

// Run the SW reset first (if requested) — it ends in `location.replace`,
// so React never mounts during the reset hop. The normal boot path runs
// inline on success.
void (async () => {
  const reset = await maybeResetServiceWorker();
  if (!reset) bootstrap();
})();
